// types
import { TransactionAugmented } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// ui
import { IconCircle, IconCircleProps, transactionIconTypes } from '../IconCircle/IconCircle';

// utils
import {
	transactionTooltipKeys,
	transactionReceivedTooltipKeys,
	transactionNotReceivedTooltipKeys,
	transactionRevertedTooltipKeys,
} from '../../../utils/translations';
import { hasFailedPayment } from '../../../utils/transactions';

// props
export interface IconCircleTransactionProps extends IconCircleProps {
	hasTooltip?: boolean;
	transaction: TransactionAugmented;
}

export const IconCircleTransaction = ({
	badges,
	hasTooltip = true,
	transaction,
	...props
}: IconCircleTransactionProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const isReverted = transaction.status === 'REVERTED';
	const isFailed = hasFailedPayment(transaction);

	// default props
	const iconCircleProps: IconCircleProps = {
		badges: [...(badges || [])],
		type: transactionIconTypes[transaction.type],
	};

	if (hasTooltip) {
		iconCircleProps.TooltipProps = {
			title: t(transactionTooltipKeys[transaction.type]),
		};
	}

	if (isReverted) {
		iconCircleProps.color = 'grey';

		if (hasTooltip) {
			iconCircleProps.TooltipProps = { title: t(transactionRevertedTooltipKeys[transaction.type]) };
		}
	}

	if (process.env.NODE_ENV !== 'development' && isFailed) {
		iconCircleProps.color = 'error';

		if (hasTooltip) {
			iconCircleProps.TooltipProps = { title: t('failed') };
		}
	}

	if (transaction.hasReimbursement) {
		if (hasTooltip) {
			let tooltipTitle = t('paymentReimbursed');

			if (transaction.type === 'CASH_BENEFIT') {
				tooltipTitle = t('cashBenefitReimbursed');
			}

			if (transaction.type === 'OPEX') {
				tooltipTitle = t('operatingExpenseReimbursed');
			}

			iconCircleProps.TooltipProps = { title: tooltipTitle };
		}

		iconCircleProps.color = 'warning';
	}

	if (transaction.isReceived === true) {
		iconCircleProps.color = 'green';
		const tooltipKey = transactionReceivedTooltipKeys[transaction.type];
		if (hasTooltip && tooltipKey) {
			iconCircleProps.TooltipProps = { title: t(tooltipKey) };
		}
	} else if (transaction.isReceived === false) {
		iconCircleProps.color = 'error';
		const tooltipKey = transactionNotReceivedTooltipKeys[transaction.type];
		if (hasTooltip && tooltipKey) {
			iconCircleProps.TooltipProps = { title: t(tooltipKey) };
		}
	}

	return <IconCircle {...props} {...iconCircleProps} />;
};
